import type { ChatType } from '@kanbu/schema';
import { AgentType } from '@kanbu/schema/enums';
import { useLingui } from '@lingui/react/macro';
import { Button, Input, Label, toast } from '@utima/ui';
import { useFieldState } from 'informed';
import { useState } from 'react';

import { Card } from '@/components/card/Card';
import { SchemaFields } from '@/components/form/SchemaFields';
import { useFieldsSchema } from '@/components/form/useFieldsSchema';
import { useAccess } from '@/hooks/useAccess';
import { trpc } from '@/services/trpc';

/**
 * Bring-your-own-Twilio, optional per chat — collapsed under the phone
 * number field, off by default. Absent means the platform Twilio account;
 * saving both fields here switches this chat's calls to the customer's own
 * account instead. See the doc comment on `Chat.twilioAccountSid` /
 * `twilioAuthToken` for the full picture.
 *
 * Deliberately its own save action, separate from the rest of the chat
 * form: the auth token is encrypted server-side and the API never returns
 * it once saved, so it cannot round-trip through the same form state as
 * everything else on this page — see `chats.setTwilioCredentials`.
 */
function TwilioCredentialsPanel({ chatId }: { chatId: string }) {
  const { t } = useLingui();
  const { data: status, refetch } =
    trpc.chats.getTwilioCredentialsStatus.useQuery({ id: chatId });

  const [accountSid, setAccountSid] = useState('');
  const [authToken, setAuthToken] = useState('');

  const setCredentials = trpc.chats.setTwilioCredentials.useMutation({
    onSuccess: async () => {
      toast.success(t`Twilio credentials saved.`);
      setAccountSid('');
      setAuthToken('');
      await refetch();
    },
    onError: error => toast.error(error.message),
  });

  const clearCredentials = trpc.chats.clearTwilioCredentials.useMutation({
    onSuccess: async () => {
      toast.success(t`Reverted to the Kanbu Twilio account.`);
      await refetch();
    },
    onError: error => toast.error(error.message),
  });

  const hasCustomCredentials = status?.hasCustomCredentials ?? false;
  const canSave = accountSid.trim().length > 0 && authToken.trim().length > 0;

  return (
    <Card title={t`Advanced`} collapsed size='sm'>
      <p className='text-xs text-muted-fg'>
        {hasCustomCredentials
          ? t`This chat's calls run on its own Twilio account. Enter both fields below to replace the credentials, or use the button below to revert to Kanbu's account.`
          : t`This chat's calls run on Kanbu's Twilio account by default. Enter a Twilio Account SID and Auth Token below to route this chat's calls through your own account instead.`}
      </p>

      {hasCustomCredentials && (
        <Button
          type='button'
          variant='secondary'
          size='sm'
          loading={clearCredentials.isPending}
          onClick={() => clearCredentials.mutate({ id: chatId })}
        >
          {t`Use Kanbu's Twilio account instead`}
        </Button>
      )}

      <div className='space-y-1.5'>
        <Label>{t`Account SID`}</Label>
        <Input
          value={accountSid}
          onChange={e => setAccountSid(e.target.value)}
          placeholder='ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx'
        />
      </div>

      <div className='space-y-1.5'>
        <Label>{t`Auth token`}</Label>
        <Input
          type='password'
          value={authToken}
          onChange={e => setAuthToken(e.target.value)}
          placeholder={
            hasCustomCredentials ? t`••••••••••••••••••••••••••••••••` : ''
          }
        />
      </div>

      <p className='text-xs text-muted-fg'>
        {t`Both fields are required to save — Twilio always presents them together, and the auth token is never shown again once saved.`}
      </p>

      <Button
        type='button'
        loading={setCredentials.isPending}
        disabled={!canSave}
        onClick={() =>
          setCredentials.mutate({ id: chatId, accountSid, authToken })
        }
      >
        {t`Save Twilio credentials`}
      </Button>
    </Card>
  );
}

export interface TwilioConfigFieldsProps {
  data?: ChatType;
}

export function TwilioConfigFields({ data }: TwilioConfigFieldsProps) {
  const { hasAccess } = useAccess();
  const { t } = useLingui();

  /**
   * Credentials for the DEFAULT (platform) account are not configured here —
   * Twilio is platform-owned, one Kanbu account whose secrets live in
   * deployment config. A chat's own account, when it brings one, is handled
   * by `TwilioCredentialsPanel` below instead — see that component's doc
   * comment.
   */
  const { schema: detailsSchema } = useFieldsSchema(
    [
      {
        name: 'voiceConfig.twilio.number',
        label: t`Phone number`,
        placeholder: '+420123456789',
        tooltip: t`The number callers dial to reach this voicebot, in international format. Kanbu provisions it for you — ask support if this chat does not have one yet.`,
      },
    ],
    [t, hasAccess],
  );

  const typeFieldState = useFieldState('agentType');

  // These fields should be visible only if the agent type is Voice
  if (typeFieldState.value !== AgentType.Voice) {
    return null;
  }

  return (
    <Card title={t`Phone number`}>
      <SchemaFields schema={detailsSchema} />
      {/*
       * Bring-your-own-Twilio needs an existing chat id (the credentials
       * mutations act on a chat by id) — a chat being created has none yet,
       * so this only shows once the chat has been saved at least once.
       */}
      {data?.id && <TwilioCredentialsPanel chatId={data.id} />}
    </Card>
  );
}
